import React, { useEffect, useState, useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faLock, faEyeSlash } from '@fortawesome/pro-solid-svg-icons';
import { useHistory } from 'react-router-dom';
import {
  faCheckCircle,
  faTimesCircle,
} from '@fortawesome/pro-regular-svg-icons';
import { Redirect, useLocation } from 'react-router-dom';
import IconButton from '../../../../components/buttons/IconButton';
import {
  ErrorMessage,
  FormContent,
  FormContentGroup,
  FormHeading,
  InputContainer,
  ResponseText,
  StyledButton,
  SuccessMessage,
} from '../styles';
import { PASSWORD_REGEX } from '../../../../constants/regex';
import { UserContext } from '../../../../stores';
import PasswordIndicator from '../../../../components/PasswordIndicator';
import { FormattedMessage, injectIntl } from 'react-intl';

const BASE_URL = process.env.REACT_APP_API_HOST_NAME;

function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

function ResetPassword({ intl: { formatMessage }, firstTimeLogin }) {
  const userStore = useContext(UserContext);
  const query = useQuery();
  const guid = query.get('guid');
  const history = useHistory();

  const [validGuid, setValidGuid] = useState(true);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handlePassword = (event) => setPassword(event.target.value);

  const handleConfirmPassword = (event) =>
    setConfirmPassword(event.target.value);

  const onPressResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    // If passwords don't match.
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    // If password isn't strong enough according to the regular expression.
    if (!PASSWORD_REGEX.test(password)) {
      setError(
        'Password must at least have a length of 8 with at least a' +
          ' symbol, upper and lower case letters and a number.'
      );
      return;
    }

    try {
      // when the user login for the first time we want them to change the password
      if (firstTimeLogin) {
        await userStore.changePasswordForFirstTime(password);
      } else {
        await userStore.changePasswordFromReset(password, guid);
      }
      history.push('/');
      setSuccess(true);
    } catch (e) {
      setError('Something went wrong.');
    }
  };

  useEffect(() => {
    const checkGuid = (GUID) => {
      // Check if the GUID parameter is an existing valid GUID.
      fetch(`${BASE_URL}/api/v1/validate-guid/`, {
        method: 'POST',
        body: JSON.stringify({
          GUID,
        }),
      })
        .then((res) =>
          res.text().then((text) => {
            if (text === 'Not a valid GUID') setValidGuid(false);
          })
        )
        .catch(() => setValidGuid(false));
    };
    // when there is not guid, it means that we are using the page to change the password
    // for the first time.. so there is no need to check the GUID
    if (guid) checkGuid(guid);
  }, [guid]);

  if (validGuid || firstTimeLogin) {
    return (
      <React.Fragment>
        <FormHeading>{formatMessage({id: "resetPassword.title"})}</FormHeading>
        <form onSubmit={onPressResetPassword}>
          <FormContent>
            <FormContentGroup>
              <label><FormattedMessage id="resetPassword.newPassword" /></label>
              <InputContainer>
                <FontAwesomeIcon icon={faLock} />
                <input
                  name="password"
                  value={password}
                  onChange={handlePassword}
                  type={showPassword ? 'input' : 'password'}
                />
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setShowPassword(!showPassword);
                  }}
                  icon={
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                  }
                />
              </InputContainer>

              <PasswordIndicator
                password={password}
              />

            </FormContentGroup>
            <FormContentGroup>
              <label><FormattedMessage id="resetPassword.confirmPassword" /></label>
              <InputContainer>
                <FontAwesomeIcon icon={faLock} />
                <input
                  name="confirmPassword"
                  value={confirmPassword}
                  onChange={handleConfirmPassword}
                  type={showConfirmPassword ? 'input' : 'password'}
                />
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setShowConfirmPassword(!showConfirmPassword);
                  }}
                  icon={
                    <FontAwesomeIcon
                      icon={showConfirmPassword ? faEyeSlash : faEye}
                    />
                  }
                />
              </InputContainer>

              {success && (
                <SuccessMessage>
                  <FontAwesomeIcon icon={faCheckCircle} />
                  <ResponseText>
                    Your password has been reset successfully.
                  </ResponseText>
                </SuccessMessage>
              )}

              {error && (
                <ErrorMessage>
                  <FontAwesomeIcon icon={faTimesCircle} />
                  <ResponseText>{error}</ResponseText>
                </ErrorMessage>
              )}
            </FormContentGroup>

            {/* <StyledButton onClick={onPressResetPassword} type="submit"> */}
            <StyledButton type="submit"><FormattedMessage id="resetPassword.resetPassword" /></StyledButton>
          </FormContent>
        </form>
      </React.Fragment>
    );
  }
  // Redirects to login page when GUID is invalid.
  return <Redirect to="/" />;
}

export default injectIntl(ResetPassword);
